import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd, isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    // TOMI — no mobile, open()/#close() já controlam o atributo [open] (via
    // reset()) de forma síncrona. Sem isto, o comportamento NATIVO do
    // navegador pra clique em <summary> (alternar [open] sozinho, como ação
    // padrão do próprio elemento) roda JUNTO com esse código, e como os
    // dois mexem no mesmo atributo, um podia desfazer o que o outro acabou
    // de fazer — foi isso que causava o menu abrir com o fundo escurecido
    // mas sem o painel aparecer (a tal "tela preta"), de forma
    // intermitente. Bloqueia só a ação padrão nativa; a lógica do próprio
    // tema (on:click="/toggle" etc.) continua rodando normalmente.
    this.addEventListener('click', this.#preventNativeToggleOnMobile, { capture: true });
    this.#setupAnimatedElementListeners();

    // TOMI — se o usuário tocou no hambúrguer ANTES deste script terminar
    // de carregar (ele é fetchpriority="low" de propósito), o navegador já
    // abriu o <details> nativamente ([open]=true) sozinho — um fallback só
    // de CSS (ver snippets/header-drawer.liquid) já deixa o painel visível
    // nesse caso, sem precisar de JS nenhum. Aqui, ao conectar, só
    // sincroniza .menu-open com esse estado que já existe, pra tudo que
    // depende da classe (foco, ARIA) ficar de acordo a partir de agora —
    // sem isso, o painel continuaria visível (o CSS não depende do JS),
    // mas focus trap/ARIA ficariam desatualizados até o próximo toque.
    if (this.refs.details?.hasAttribute('open')) {
      this.refs.details.classList.add('menu-open');
      this.refs.details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
      trapFocus(this.refs.details);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#preventNativeToggleOnMobile, { capture: true });
  }

  /**
   * @param {MouseEvent} event
   */
  #preventNativeToggleOnMobile = (event) => {
    if (!isMobileBreakpoint()) return;
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('summary')) event.preventDefault();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);

    // TOMI — no mobile o menu não tem NENHUMA animação (ver
    // snippets/header-drawer.liquid, bloco @media max-width:749px) — o
    // requestAnimationFrame + onAnimationEnd abaixo existem só pra
    // sincronizar com uma animação de slide que ali nem roda. Esperar um
    // frame antes de aplicar .menu-open criava uma janela onde o atributo
    // nativo [open] do <summary> (que o navegador já aplica na hora, de
    // graça) ficava true mas a classe que realmente mostra o painel ainda
    // não — um toque duplo/rápido nessa janela podia deixar o backdrop
    // "aberto" sem o painel do menu, a tal tela preta relatada. No mobile,
    // aplica tudo no mesmo tick, sem esperar nada.
    if (isMobileBreakpoint()) {
      // O clique no <summary> tem sua ação padrão nativa bloqueada no
      // mobile (ver #preventNativeToggleOnMobile) especificamente pra
      // evitar que ela mexa nesse mesmo atributo em paralelo — então agora
      // é este código, e só ele, que define o estado "aberto" de verdade.
      details.setAttribute('open', '');
      details.classList.add('menu-open');
      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }
      trapFocus(details);
      return;
    }

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // TOMI — mesmo motivo do open(): no mobile não existe animação
    // nenhuma, então não há nada pra esperar. Fecha tudo no mesmo tick.
    if (isMobileBreakpoint()) {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
      return;
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
